import { useIdleTimer } from 'react-idle-timer';
import type { PresenceType } from 'react-idle-timer';
import { useContext, useEffect, useRef, useState } from 'react';
import { TherapistActivityReactContext } from '../App';
import { useActor } from '@xstate/react';
import { TherapistActivityService } from '../machines/therapistActivityMachine';
import { clientChangeEvents } from './activityEvents';

/**
 * RTM page-view tracking. E57 (Q-e57g1-2 A, rule 2): a page that knows its client passes it here and it rides the
 * machine's LOADED event (`clientId`, which `addNewActivity` already prefers over the global filter) — the client page no
 * longer sets and clears the header's global `filterClientId` on mount and leave, so a page view is recorded for the
 * client it shows and the bar's own filter is left alone. `enabled: false` lets an EMBEDDED list (a client-page section)
 * leave the tracking to the page that hosts it.
 */
export const useActivityTracking = (
  resource = '',
  clientId?: string | null,
  enabled = true,
) => {
  const therapistActivityService = useContext(TherapistActivityReactContext);
  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );

  const [previousResource, setPreviousResource] = useState('');

  useEffect(() => {
    if (!enabled) return;
    const previousPrefix = previousResource.split('/')[0];
    const currentPrefix = resource.split('/')[0];
    if (
      !!resource &&
      previousPrefix === currentPrefix &&
      previousResource !== resource
    ) {
      therapistActivitySend({
        type: 'RESOURCE_UPDATED',
        resource,
      });
    }

    setPreviousResource(resource);
  }, [resource]);

  useEffect(() => {
    if (!enabled) return undefined;
    therapistActivitySend({
      type: 'LOADED',
      resource,
      ...(clientId ? { clientId } : {}),
    });

    return () => {
      therapistActivitySend({
        type: 'LEFT',
      });
      setPreviousResource('');
    };
  }, []);

  // the client arrives after the first render (the page's record loads) — the running activity takes it; a DIFFERENT
  // client under the same mounted page (A → B: back / forward, Find a client) ends A's activity for A and starts B's
  const trackedClientId = useRef<string | null | undefined>(clientId);
  useEffect(() => {
    if (!enabled) return;
    const events = clientChangeEvents(resource, trackedClientId.current, clientId);
    if (clientId) trackedClientId.current = clientId;
    events.forEach((e) => therapistActivitySend(e));
  }, [clientId]);

  const onPresenceChange = (presence: PresenceType) => {
    if (!enabled) return;
    const isIdle = presence.type === 'idle';
    const isActive = presence.type === 'active' && !presence.prompted;
    const isPrompted = presence.type === 'active' && presence.prompted;
    if (isIdle) {
      therapistActivitySend({
        type: 'TIMED_OUT',
        resource,
      });
    }
    if (isActive) {
      therapistActivitySend({
        type: 'RESUMED',
        resource,
      });
    }
  };

  useIdleTimer({
    onPresenceChange,
    timeout: 60_000,
  });
};
